import { FC, useState } from 'react';
import { BookOpen, Plus } from 'lucide-react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import type { UserCourse } from '@teachur/http-client';

import { Button } from '@new/common/ui';
import { Loader } from '@new/common/ui/Loader/Loader';

import { AddCourseModal } from '../AddCourseModal/AddCourseModal';
import { BulkActionsBar } from './BulkActionsBar';
import { CourseListItem } from './CourseListItem';
import { CourseDetailPanel } from './CourseDetailPanel';
import { ConfirmCourseModal } from './ConfirmCourseModal';
import { CourseEmptyState } from './CourseEmptyState';

import { useStudentCourses } from '../../hooks/useStudentCourses';
import { useCourseSelection } from '../../hooks/useCourseSelection';
import { useCourseActions } from '../../hooks/useCourseActions';

interface StudentCoursesTabProps {
  studentId: number;
}

export const StudentCoursesTab: FC<StudentCoursesTabProps> = ({ studentId }) => {
  const [activeUserCourse, setActiveUserCourse] = useState<UserCourse | null>(null);
  const [isAddCourseOpen, setIsAddCourseOpen] = useState(false);

  const { courses, isLoading, isAdmin, refetchCourses } = useStudentCourses(studentId);

  const {
    selectedIds,
    selectedCourses,
    selectedCount,
    isAllSelected,
    isIndeterminate,
    toggleSelect,
    selectAll,
    clearSelection,
  } = useCourseSelection(courses);

  const {
    actionTarget,
    isPending,
    openSingleDelete,
    openSingleStart,
    openSingleStop,
    openBulkDelete,
    openBulkStart,
    openBulkStop,
    closeActionModal,
    confirmAction,
  } = useCourseActions({
    selectedCourses,
    refetchCourses,
    clearSelection,
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col gap-4">
      <AddCourseModal
        isOpen={isAddCourseOpen}
        onClose={() => setIsAddCourseOpen(false)}
        userId={studentId}
      />

      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <div className="flex min-h-0 flex-col gap-2 lg:h-0 lg:min-h-full lg:self-stretch lg:overflow-hidden">
          <div className="flex shrink-0 items-center justify-between py-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-(--primary-O900)" />

              <h3 className="[font-family:var(--font-arvo)] leading-tight tracking-tight [font-size:var(--fs-h5)] m-0! font-bold text-(--neutral-black-NB1000)!">
                Enrolled Courses
              </h3>

              <span className="text-sm text-(--neutral-black-NB700)">({courses.length})</span>
            </div>

            {isAdmin && (
              <Button variant="primary" size="sm" onClick={() => setIsAddCourseOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Course
              </Button>
            )}
          </div>
          {courses.length === 0 ? (
            <CourseEmptyState />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden rounded-xl border bg-(--neutral-black-NB100)/50 p-4">
              {isAdmin && (
                <BulkActionsBar
                  totalCount={courses.length}
                  selectedCount={selectedCount}
                  isAllSelected={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  onSelectAll={selectAll}
                  onBulkDelete={openBulkDelete}
                  onBulkStart={openBulkStart}
                  onBulkStop={openBulkStop}
                />
              )}

              <ScrollArea.Root className="min-h-0 flex-1 overflow-hidden rounded-lg">
                <ScrollArea.Viewport className="h-full w-full">
                  <div className="flex flex-col gap-2">
                    {courses.map((userCourse) => (
                      <CourseListItem
                        key={userCourse.id}
                        userCourse={userCourse}
                        isSelected={selectedIds.has(userCourse.id)}
                        isActive={activeUserCourse?.id === userCourse.id}
                        isAdmin={isAdmin}
                        onToggleSelect={toggleSelect}
                        onRowClick={setActiveUserCourse}
                        onStart={openSingleStart}
                        onStop={openSingleStop}
                        onDelete={openSingleDelete}
                      />
                    ))}
                  </div>
                </ScrollArea.Viewport>

                <ScrollArea.Scrollbar
                  orientation="vertical"
                  className="flex w-2.5 bg-transparent p-0.5 transition-colors hover:bg-(--neutral-black-NB100)/50"
                >
                  <ScrollArea.Thumb className="flex-1 cursor-pointer rounded-full bg-(--neutral-black-NB300) transition-colors hover:bg-(--neutral-black-NB500)" />
                </ScrollArea.Scrollbar>

                <ScrollArea.Corner />
              </ScrollArea.Root>
            </div>
          )}
        </div>

        {courses.length > 0 && (
          <div className="min-h-[60vh] sticky top-4 bg-(--neutral-white-NW1000)">
            {activeUserCourse ? (
              <CourseDetailPanel userCourse={activeUserCourse} studentId={studentId} />
            ) : (
              <CourseEmptyState
                title="Select a course to see details"
                description="Choose a course from the list to view its status, hearing notes, and additional details."
              />
            )}
          </div>
        )}
      </div>
      {actionTarget && (
        <ConfirmCourseModal
          isOpen={!!actionTarget}
          action={actionTarget.action}
          courseNames={actionTarget.courses.map((userCourse) => userCourse.course?.name ?? '—')}
          isPending={isPending}
          onConfirm={confirmAction}
          onClose={closeActionModal}
        />
      )}
    </div>
  );
};
